import { Device } from '../models/Device';

export type ProvisioningMode = 'ble' | 'softap';

export interface WifiNetwork {
    ssid: string;
    rssi?: number;
    auth?: number;
    cipher?: number;
    channel?: number;
}

export interface ProvisioningCandidate {
    mode: ProvisioningMode;
    id: string;
    name: string;
    deviceId?: string;
    rssi?: number;
}

export interface WifiJoinOptions {
    /** 2.4 GHz channel (1-14) to pin the device's join to, skipping its join-scan. */
    channel?: number;
    signal?: AbortSignal;
}

export interface ProvisioningTransport {
    readonly mode: ProvisioningMode;
    readonly displayName: string;
    readonly deviceId?: string;
    readonly checkConnectivityOnClaim?: boolean;

    prepare?(signal?: AbortSignal): Promise<void>;
    /**
     * Resolves once the device enters setup mode. Offered by transports that can
     * observe the device's provisioning state after prepare() failed with
     * 'device_not_in_setup_mode'. Resolves connected and ready to scan.
     */
    waitForSetupMode?(signal?: AbortSignal): Promise<void>;
    scanWifiNetworks(signal?: AbortSignal): Promise<WifiNetwork[]>;
    joinWifiNetwork(ssid: string, password: string, options?: WifiJoinOptions): Promise<void>;
    cleanup(): Promise<void>;
}

/** The firmware only accepts pinned joins on 2.4 GHz channels 1-14. */
export function pinnableWifiChannel(channel: number | undefined): number | undefined {
    return channel !== undefined && Number.isInteger(channel) && channel >= 1 && channel <= 14
        ? channel
        : undefined;
}

export function normalizeNetworks(networks: WifiNetwork[]): WifiNetwork[] {
    const bySsid = new Map<string, WifiNetwork>();

    for (const network of networks) {
        if (!network.ssid) {
            continue;
        }

        const existing = bySsid.get(network.ssid);
        if (!existing || (network.rssi ?? -Infinity) > (existing.rssi ?? -Infinity)) {
            bySsid.set(network.ssid, network);
        }
    }

    return Array.from(bySsid.values()).sort((a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity));
}

// The setup code is the device's MAC; its product code follows from the MAC block.
export function deviceIdFromSetupCode(setupCode: string): string {
    return Device.toDeviceId(setupCode);
}
